import { useQuery } from '@tanstack/react-query';
import { getPendingApprovalCounts } from '@/api/attendanceRequests';
import { useAuth } from '@/hooks/useAuth';

/** Where an approval count leads: the requests list, opened on Pending. */
export const PENDING_REQUESTS_PATH = '/andone/admin/requests?status=Pending';

/**
 * How many requests are waiting on this user's approval — a reporting
 * manager's own reportees, or the wider queue for a global / HR approver. The
 * endpoint does the scoping, so the number is exactly what they can act on.
 *
 * Shares its cache key with the dashboard's approvals card, so the sidebar,
 * the topbar and the dashboard never disagree, and an approval that
 * invalidates the key updates all three.
 */
export function usePendingApprovalCount(): number {
  const { user } = useAuth();
  const permissions = user?.permissions ?? [];
  const canApprove = permissions.includes('requests:approve') || permissions.includes('requests:hr-approve');

  const { data } = useQuery({
    queryKey: ['approval-pending-counts'],
    queryFn: getPendingApprovalCounts,
    enabled: canApprove,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  return canApprove ? data?.total ?? 0 : 0;
}
